import { MyDesignQR } from '../models/MyDesignQR';

// NOTE: フォーマットは
// https://spdlybra.nintendo.co.jp/confluence/pages/viewpage.action?pageId=706029234
// https://spdlybra.nintendo.co.jp/confluence/pages/viewpage.action?pageId=526382491
// を参照

type MyDesignType = 'normal' | 'pro' | 'unknown';

export function analyzeQrBinary(base64EncodeText: string): MyDesignQR | undefined {
  const buffer = base64ToArrayBuffer(base64EncodeText);
  const myDesignType = detectMyDesignType(buffer);

  try {
    const ubin = newUbin(buffer, myDesignType);
    switch (myDesignType) {
      case 'normal': {
        const usage = readNumber(ubin, 105, 1);
        // NOTE: normalの場合でもusage(用途)でproの場合が存在するため確認が必要。 e.g. 角のぼうし
        const checkedMyDesignType = checkDesignTypeByUsage(usage);
        return {
          title: readString(ubin, 0, 42),
          playerId: readNumber(ubin, 42, 2),
          creator: readString(ubin, 44, 18),
          sex: readNumber(ubin, 62, 1),
          villageId: readNumber(ubin, 64, 2),
          village: readString(ubin, 66, 18),
          language: readNumber(ubin, 84, 1),
          country: readNumber(ubin, 86, 1),
          region: readNumber(ubin, 87, 1),
          paletteColors: ubin.subarray(88, 88 + 15),
          color: readNumber(ubin, 103, 1),
          looks: readNumber(ubin, 104, 1),
          usage: readNumber(ubin, 105, 1),
          colorData: ubin.subarray(108, 108 + 512),
          designType: checkedMyDesignType,
          qrText: base64EncodeText
        };
      }
      case 'pro': {
        const consecutiveId = extractConsecutiveId(buffer);
        const sheetIndex = detectSheets(buffer);

        const common = {
          designType: myDesignType,
          sheetIndex,
          qrText: base64EncodeText,
          consecutiveId
        };

        if (sheetIndex === 0) {
          return {
            ...common,
            title: readString(ubin, 0, 42),
            playerId: readNumber(ubin, 42, 2),
            creator: readString(ubin, 44, 18),
            sex: readNumber(ubin, 62, 1),
            villageId: readNumber(ubin, 64, 2),
            village: readString(ubin, 66, 18),
            language: readNumber(ubin, 84, 1),
            country: readNumber(ubin, 86, 1),
            region: readNumber(ubin, 87, 1),
            paletteColors: ubin.subarray(88, 88 + 15),
            color: readNumber(ubin, 103, 1),
            looks: readNumber(ubin, 104, 1),
            usage: readNumber(ubin, 105, 1),
            colorData: ubin.subarray(108, 108 + 432)
          };
        } else if (sheetIndex === 1 || sheetIndex === 2) {
          return {
            ...common,
            colorData: ubin.subarray(0, 540)
          };
        } else if (sheetIndex === 3) {
          return {
            ...common,
            // 4枚目は末尾に4bytesのpaddingが含まれるため
            colorData: ubin.subarray(0, 536)
          };
        } else {
          throw new Error('invalid sheet index');
        }
      }
      default:
        throw new Error('invalid as my design');
    }
  } catch {
    return undefined;
  }
}

export function base64ToArrayBuffer(base64EncodedString: string): Uint8Array {
  const b = window.atob(base64EncodedString);
  const length = b.length;
  const buffer = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    buffer[i] = b.charCodeAt(i);
  }
  return buffer;
}

export function detectMyDesignType(buffer: Uint8Array): MyDesignType {
  if (isNormal(buffer)) {
    return 'normal';
  } else if (isPro(buffer)) {
    return 'pro';
  } else {
    return 'unknown';
  }
}

export function extractConsecutiveId(buffer: Uint8Array): string {
  const [, c1, c2] = Array.from(buffer);
  return String((c1 << 4) + (c2 >> 4));
}

type SheetIndex = 0 | 1 | 2 | 3;

export function detectSheets(proBuffer: Uint8Array): SheetIndex {
  const c0 = proBuffer[0];
  const code = c0.toString(16).padStart(2, '0');
  if (code.charAt(0) !== '3') {
    throw new Error('invalid as my design pro header format');
  }
  const x = code.charAt(1);
  const n = parseInt(x, 10);
  if (n < 0 || 3 < n) {
    throw new Error('invalid as my design pro sheet index');
  }
  return n as SheetIndex;
}

function isNormal(buffer: Uint8Array) {
  if (buffer.length !== 627) {
    return false;
  }

  const [c0, c1, c2] = Array.from(buffer);
  return (
    c0.toString(16) === '40' &&
    c1.toString(16) === '26' &&
    c2
      .toString(16)
      .padStart(2, '0')
      .charAt(0)
      .toUpperCase() === 'C'
  );
}

function isPro(buffer: Uint8Array) {
  if (buffer.length !== 563) {
    return false;
  }

  const [c0, c1, c2, c3, c4] = Array.from(buffer);
  return (
    c0
      .toString(16)
      .padStart(2, '0')
      .charAt(0) === '3' &&
    c1
      .toString(16)
      .padStart(2, '0')
      .charAt(0) === '3' &&
    c2
      .toString(16)
      .padStart(2, '0')
      .charAt(1) === '4' &&
    c3.toString(16).padStart(2, '0') === '02' &&
    c4.toString(16).toUpperCase() === '1C'
  );
}

export function checkDesignTypeByUsage(usage: number) {
  switch (usage) {
    case 0:
    case 1:
    case 2:
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 8:
      return 'pro';
    case 9:
    case 10:
    case 11:
    default:
      return 'normal';
  }
}

export function newUbin(buffer: Uint8Array, type: MyDesignType): Uint8Array {
  if (type === 'pro') {
    // NOTE: プロの場合は5バイト目から540バイト
    return buffer.subarray(5, 545);
  } else if (type === 'normal') {
    // NOTE: ノーマルの場合は2.5バイト目から620バイト
    const [, , c2, ...rest] = Array.from(buffer).map(n => n.toString(16).padStart(2, '0'));
    const code = c2.toUpperCase();
    if (code.charAt(0) !== 'C') {
      throw new Error('invalid as my design normal');
    }
    const x = code.charAt(1);
    const strBin = [x, ...rest].join('');

    // 'ABCD...' => ['AB', 'CD', ...]に変換
    const r = new RegExp('.{1,2}', 'g');
    const strBinArray = strBin.match(r) as string[];

    // 16進表記を10進に戻してUint8Array型に戻す
    return new Uint8Array(strBinArray.map(b => parseInt(b, 16))).subarray(0, 620);
  } else {
    throw new Error('invalid as my design');
  }
}

function readString(ubin: Uint8Array, offset: number, length: number): string {
  const subbin = ubin.subarray(offset, offset + length);
  const str = new TextDecoder('utf-16le', { ignoreBOM: true }).decode(subbin);
  return str.includes('\u0000') ? str.split('\u0000')[0] : str;
}

export function readNumber(ubin: Uint8Array, offset: number, length: number): number {
  const subbin = ubin.subarray(offset, offset + length);
  let num = 0;
  subbin.forEach(ub => {
    num = (num << 8) + ub;
  });
  return num;
}
